import { Info } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { CategoryNamesFit } from "./CategoryNamesFit";
import type { BiKeywordSovSummary } from "@/resources/brand-intelligence";

/** What the cards describe: a niche (its category names), one category, or every category. */
export type SovScope =
  | { kind: "niche"; names: string[] }
  | { kind: "category"; name: string }
  | { kind: "all" };

interface KeywordSovCardsProps {
  scope: SovScope;
  brand: string;
  summary: BiKeywordSovSummary;
}

const formatPct = (value: number | null): string => (value === null ? "—" : `${value.toFixed(1)}%`);

const SovCard = ({ label, value, tooltip }: { label: string; value: string; tooltip: string }) => (
  <Card className="flex-1 min-w-0">
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-3">
        <p className="text-sm text-muted-foreground">{label}</p>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger className="cursor-help" aria-label={`About ${label}`} onClick={(e) => e.preventDefault()}>
              <Info className="h-3.5 w-3.5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[200px]">
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <p className="text-2xl font-semibold text-foreground tracking-tight">{value}</p>
    </CardContent>
  </Card>
);

const trackedTooltip = (scope: SovScope, brand: string): string => {
  const held = `where ${brand} holds at least one top organic position, out of all tracked keywords`;
  if (scope.kind === "niche") return `Keywords in this niche's ${scope.names.length} categories ${held} in the niche.`;
  if (scope.kind === "all") return `Keywords in all categories ${held}.`;
  return `Keywords in ${scope.name} ${held} in the category.`;
};

const sovTooltip = (scope: SovScope, brand: string): string => {
  const weighted = "weighted by each keyword's search volume.";
  if (scope.kind === "niche") return `${brand}'s share of top organic positions across this niche's ${scope.names.length} categories, ${weighted}`;
  if (scope.kind === "all") return `${brand}'s share of top organic positions across all categories, ${weighted}`;
  return `${brand}'s share of top organic positions in ${scope.name}, ${weighted}`;
};

export const KeywordSovCards = ({ scope, brand, summary }: KeywordSovCardsProps) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
    <Card className="min-w-0">
      <CardContent className="p-4">
        <p className="text-sm text-muted-foreground mb-3">Category/ Niche</p>
        <p className="text-2xl font-semibold text-foreground tracking-tight">
          {scope.kind === "niche" ? (
            <CategoryNamesFit names={scope.names} />
          ) : scope.kind === "all" ? (
            "all categories"
          ) : (
            scope.name
          )}
        </p>
      </CardContent>
    </Card>
    <SovCard
      label="Tracked keywords"
      value={`${summary.ranked_count} of ${summary.keyword_count}`}
      tooltip={trackedTooltip(scope, brand)}
    />
    <SovCard
      label="Volume-weighted SOV"
      value={formatPct(summary.volume_weighted_sov)}
      tooltip={sovTooltip(scope, brand)}
    />
  </div>
);
